import React, { FC, Fragment, useContext, useEffect, useState } from "react"
import CustomDayPicker from "../../../date-time/CustomDayPicker"
import Loader from "../../../ui/loader"
import moment from "moment-timezone"
import Select from "react-select"
import TimeOptions from "./TimeOptions"
import useScrollToTop from "../../../../hooks/useScrollToTop"
import { add, format } from "date-fns"
import { AvailabilityRulesData } from "../../../../types/availabilityTypes"
import { Button } from "../../../ui/button"
import { CalendarRequests } from "../../../../api/app.service"
import { CommonFunctionCtx } from "../../../../context/commonFunctionContext"
import { defaultAvailabilityOption } from "../../../../objects/availabilityObjects"
import { GetAvailability } from "../../../../models/app.interface"
import { durations } from "../../../../objects/timeObjects"
import { Meeting } from "../../../../types/meetingTypes"
import { UserCtx } from "../../../../context/userContext"
import { useLocation } from "react-router-dom"
import { useToast } from "../../../../hooks/useToast"

type Props = {
  setStep: any
  newMeetingData: Meeting
  setNewMeetingData: any
  /** Availabilities for current logged in user */
  availabilities: Array<AvailabilityRulesData>
  coachDetails: any
  meeting?: any
}

const CheckAvailability: FC<Props> = ({
  newMeetingData,
  setNewMeetingData,
  setStep,
  availabilities,
  coachDetails,
  meeting,
}) => {
  const normalizeDate = (d: Date) => moment(d).startOf("day").toDate()
  const [activeDate, setActiveDate] = useState<Date>(
    newMeetingData.date
      ? normalizeDate(newMeetingData.date)
      : normalizeDate(new Date())
  )
  const [startTime, setStartTime] = useState<Date | null>(null)
  const [availabilityOptions, setAvailabilityOptions] = useState<Array<any>>(
    defaultAvailabilityOption
  )
  const [activeAvailability, setActiveAvailability] =
    useState<AvailabilityRulesData | null>(null)
  const { user } = useContext(UserCtx)
  const { setPopupNotification } = useContext(CommonFunctionCtx)
  const { toast } = useToast()
  const [availablePeriods, setAvailablePeriods] = useState<Array<any>>([])
  const [loadingAvailablePeriods, setLoadingAvailablePeriods] =
    useState<boolean>(false)
  const [isAvailChange, setisAvailChange] = useState<boolean>(false)
  const [coachIsScheduling, setCoachIsScheduling] = useState<boolean>(false)
  const [bookingWindow, setBookingWindow] = useState<number>(0)
  const defaultColor = "#4750F5"
  const location = useLocation()
  const flagged = location.state?.flag
  const service = location.state?.service
  const notOwner = location.state?.notOwner || service?.coach_user_id !== user.user_id


  const durationDefault = 60
  /**
   * Sets availabile periods based on user availability and coach availability attached to a service
   */

  const getAvailability = () => {
    setLoadingAvailablePeriods(true)

    const availabilityRuleIds = activeAvailability?.id
      ? [activeAvailability.id.toString()]
      : []

    const profileIds = newMeetingData.contacts.map((c: any) => c.profile_id)

    if (!profileIds.includes(parseInt(user.activeProfileId))) {
      profileIds.push(parseInt(user.activeProfileId))
    }

    const now = moment.utc()
    const activeDay = moment(activeDate).startOf("day")
    const bookingThreshold = now.clone().add(bookingWindow, "hours")

    let startDate: moment.Moment

    if (activeDay.isSame(bookingThreshold, "day")) {
      startDate = bookingThreshold
    } else if (activeDay.isBefore(bookingThreshold, "day")) {
      setAvailablePeriods([])
      setLoadingAvailablePeriods(false)
      return
    } else {
      startDate = activeDay
    }

    const endDateUtc = startDate.clone().endOf("day")
    const getAvailabilityRequest: GetAvailability = {
      profile_ids: profileIds,
      creator_profile_id: parseInt(coachDetails.coachProfileId),
      availability_rule_ids: availabilityRuleIds,
      meeting_duration: newMeetingData.duration || durationDefault,
      start_date: startDate.toISOString(),
      end_date: endDateUtc.toISOString(),
      coach_user_id: coachDetails.coachUserId,
      show_all_times: true,
    }

    CalendarRequests.getAvailability(getAvailabilityRequest)
      .then((data) => {
        setAvailablePeriods(data.available_periods)
        setLoadingAvailablePeriods(false)
      })
      .catch((ex) => {
        console.error(ex)
        toast.error(ex.response.data.message)
        setLoadingAvailablePeriods(false)
      })
  }

  useEffect(() => {
    if (
      newMeetingData?.coach_user_id &&
      user?.user_id === newMeetingData.coach_user_id.toString()
    ) {
      setCoachIsScheduling(true)
    }
  }, [user, newMeetingData])

  useEffect(() => {
    setNewMeetingData(() => {
      return { ...newMeetingData, date: activeDate }
    })
  }, [activeDate])

  useEffect(() => {
    if (startTime) {
      setNewMeetingData(() => {
        return {
          ...newMeetingData,
          startTime: startTime,
          endTime: add(startTime, {
            minutes: newMeetingData.duration || durationDefault,
          }),
        }
      })
    }
  }, [startTime])

  useEffect(() => {
    if (!availabilities?.length) return

    const defaultAvailability = availabilities.find((a) => a.is_default)
    const existingServiceAvailability =
      newMeetingData?.service_details?.availability_rule_id

    const matchedAvailability =
      availabilities.find((a) => a.id === existingServiceAvailability) ||
      defaultAvailability ||
      availabilities[0]

    if (!isAvailChange) {
      setActiveAvailability(matchedAvailability)
    }

    let window = 0

    if (!meeting) {
      if (notOwner || flagged !== undefined) {
        window = matchedAvailability?.min_book_window / 60
      }
    } else {
      if (!meeting.isCreator || notOwner) {
        window = matchedAvailability?.min_book_window / 60
      }
    }

    setBookingWindow(window)

    setAvailabilityOptions(
      availabilities.map((a) => ({
        value: a.name,
        label: a.name,
      }))
    )
  }, [availabilities, meeting, notOwner, flagged])

  useScrollToTop()

  const handleAvailabilityOptionSelect = (e: any) => {
    const matched = availabilities.find((a) => a.name === e.value)
    if (matched) {
      setisAvailChange(true)
      setActiveAvailability(matched)
    }
  }

  useEffect(() => {
    if (activeAvailability) {
      getAvailability()
    }
  }, [activeDate, activeAvailability, newMeetingData.duration])

  const handleNextButtonClick = () => {
    if (!startTime) {
      setPopupNotification({
        show: true,
        title: "Select Time",
        message: "Please select a time to continue.",
        callback: null,
      })
      return
    }
    setStep((previousStep: number) => previousStep + 1)
  }

  useEffect(() => {
    if (
      user.activeProfile === "coach" &&
      (window.location.pathname === "/coach/scheduling" ||
        window.location.pathname === "/member/scheduling")
    ) {
      if (startTime) {
        localStorage.setItem("selectedStartTime", JSON.stringify(startTime))
      }
    } else {
      localStorage.removeItem("selectedStartTime")
    }
  }, [startTime, window.location.pathname, user.activeProfile])

  useEffect(() => {
    if (user.activeProfile === "coach") {
      const currentPath = window.location.pathname

      if (
        currentPath === "/coach/scheduling" ||
        currentPath === "/member/scheduling"
      ) {
        const storedTime = localStorage.getItem("selectedStartTime")
        if (storedTime) {
          setStartTime(new Date(JSON.parse(storedTime)))
        }
      }
    }
  }, [user.activeProfile])

  return (
    <div className="w-full min-w-[330px] max-w-[75vw] pb-[180px] md:pb-0">
      <div className="mb-[24px] flex w-full items-center justify-between md:mb-[32px]">
        <h3 className="text-[18px] font-bold md:text-[28px] md:font-semibold">
          Check Availability
        </h3>
        {flagged && (
          <Button
            onClick={() => setStep((previousStep: number) => previousStep - 1)}
            className="text-md"
            size={"lg"}
          >
            Back
          </Button>
        )}
        <Button
          onClick={handleNextButtonClick}
          className="hidden md:block text-md"
          size={"lg"}
        >
          Next
        </Button>
      </div>
      <p></p>
      <div className="grid gap-[12px] md:grid-cols-2 md:gap-[48px]">
        <div className="w-full">
          {coachIsScheduling && availabilities?.length > 0 && (
            <div className="mb-[16px] md:mb-[24px]">
              <h4 className="text-graySlate mb-[8px] text-base">
                Availability
              </h4>
              <Select
                className="basic-single w-full"
                classNamePrefix="select"
                value={availabilityOptions.find((a) => {
                  return a.value === activeAvailability?.name
                })}
                options={availabilityOptions}
                name="availability"
                onChange={handleAvailabilityOptionSelect}
              />
            </div>
          )}

          <div className="flex justify-center px-[20px]">
            <CustomDayPicker
              activeDate={activeDate}
              setActiveDate={setActiveDate}
              disablePastDates={true}
              dayColor={defaultColor}
              bookingWindow={bookingWindow}
              meeting={meeting}
            />
          </div>
        </div>
        <div className="promotion-shadow flex w-full flex-col rounded-[16px] bg-white px-[24px]">
          <h4 className="mx-auto mb-[16px] mt-[24px] text-[16px] font-bold">
            {format(activeDate, "iiii, MMMM do")}
          </h4>
          <div className="mb-[24px]">
            <h4 className="text-graySlate mb-[8px] text-base">Duration</h4>
            <div className="">
              {newMeetingData.duration
                ? durations.find(
                    (option) => option.value === newMeetingData.duration
                  )?.label
                : durations.find((d) => d.value === durationDefault)?.label}
            </div>
          </div>
          <div className="mb-[24px]">
            <h4 className="text-graySlate mb-[8px] text-base">Time</h4>
            <div className="flex max-h-[240px] w-full flex-col gap-[8px] overflow-y-auto">
              {loadingAvailablePeriods ? (
                <div className="px-[8px] pt-[24px] text-center font-bold">
                  <Loader />
                  <h3 className="text-center text-[16px] font-bold">
                    Loading available meeting times.
                  </h3>
                </div>
              ) : (
                <>
                  {availablePeriods.length > 0 ? (
                    <>
                      {availablePeriods.map((availablePeriod, idx) => {
                        return (
                          <Fragment key={idx}>
                            <TimeOptions
                              availabilityStart={moment(
                                availablePeriod.start
                              ).toDate()}
                              availabilityEnd={moment(
                                availablePeriod.end
                              ).toDate()}
                              duration={
                                newMeetingData.duration || durationDefault
                              }
                              setStartTime={setStartTime}
                              startTime={newMeetingData.startTime}
                              dayColor={defaultColor}
                              // bookingWindow={bookingWindow}
                            />
                            <div className="bg-hover text-graySlate flex items-center justify-center rounded-[10px] p-[12px] text-base font-bold last:hidden">
                              Unavailable
                            </div>
                          </Fragment>
                        )
                      })}
                    </>
                  ) : (
                    <div className="px-[8px] pt-[40px] text-center font-bold">
                      No available meeting times for you and your meeting
                      participant(s) on this day. Please select another day.
                    </div>
                  )}
                  <div className="text-center font-bold">
                    Meeting Times shown in{" "}
                    {moment().tz(newMeetingData.timezone.value).format("z")}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        <Button
          onClick={handleNextButtonClick}
          className="md:hidden text-md"
          size={"lg"}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

export default CheckAvailability
